/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
import { Card, CardContent } from '@skyxops/ui';
import { Building2, LayoutGrid, Orbit, type LucideIcon } from 'lucide-react';
import { Tile as TileType } from '../api';
import { buildTileHref } from './tileHref';

interface Props {
  tile: TileType;
}

// Standard per-app icons (keyed by app_id) instead of a letter avatar.
const APP_ICONS: Record<string, LucideIcon> = {
  launcher: LayoutGrid,
  launchpad: LayoutGrid,
  msp: Building2,
  orbit: Orbit,
};

// AI-LAUNCH-49: this file used to carry its own private buildHref(), byte-identical
// to buildTileHref in ./tileHref — which had been extracted precisely so the tile
// anchor and the command palette shared ONE copy of the token-in-URL logic, but the
// tile was never switched over. Two copies of security-relevant code drift; there is
// now one.

export function Tile({ tile }: Props) {
  return (
    <a
      href={buildTileHref(tile.route_prefix)}
      className="group block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-skyxops-lilac"
    >
      <Card className="h-full transition-all hover:border-skyxops-lilac hover:shadow-md">
        <CardContent className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-skyxops-lilac/20 font-bold text-skyxops-lilac">
            {(() => {
              const Icon = APP_ICONS[tile.app_id];
              return Icon ? <Icon className="h-5 w-5" /> : tile.name.charAt(0).toUpperCase();
            })()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-foreground group-hover:text-skyxops-lilac">
              {tile.name}
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">
              {tile.route_prefix}
            </div>
          </div>
        </CardContent>
      </Card>
    </a>
  );
}
